/**
 * Draws a liquidity segment chart on the provided container using ECharts
 * @param {HTMLElement} canvas - The container element for the chart (will be repurposed)
 * @param {Object} liquidityInfo - The liquidity information containing segments data
 */
export const drawLiquiditySegmentChart = (canvas, liquidityInfo) => {
  if (!liquidityInfo || !liquidityInfo.liquidssegments || liquidityInfo.liquidssegments.length === 0) {
    return;
  }
  
  // Import formatNumberM
  const { formatNumberM } = require('./formatNumber');
  
  // Import echarts
  const echarts = require('echarts');
  
  // Clear any existing chart
  let chart = echarts.getInstanceByDom(canvas);
  if (chart) {
    chart.dispose();
  }
  
  // Initialize ECharts instance
  chart = echarts.init(canvas);
  
  const segments = liquidityInfo.liquidssegments;
  
  // Find min and max price for scaling
  let minPrice = Number.MAX_VALUE;
  let maxPrice = Number.MIN_VALUE;
  
  segments.forEach(segment => {
    const startPrice = parseFloat(segment.startPrice);
    const endPrice = parseFloat(segment.endPrice);
    
    if (!isNaN(startPrice)) {
      minPrice = Math.min(minPrice, startPrice);
    }
    
    if (!isNaN(endPrice)) {
      maxPrice = Math.max(maxPrice, endPrice);
    }
  });
  
  // Add some padding to the price range
  const pricePadding = (maxPrice - minPrice) * 0.05;
  minPrice -= pricePadding;
  maxPrice += pricePadding;
  
  // Calculate optimal number of splits for x-axis based on price range
  const priceRange = maxPrice - minPrice;
  let splitNumber = 10; // Default number of splits
  
  // Adjust split number based on price range magnitude
  if (priceRange < 0.0001) {
    splitNumber = 5;
  } else if (priceRange < 0.001) {
    splitNumber = 8;
  } else if (priceRange < 0.01) {
    splitNumber = 10;
  } else if (priceRange < 0.1) {
    splitNumber = 12;
  } else {
    splitNumber = 15;
  }
  
  // Prepare data for the chart - structure data properly for custom rendering
  const upwardSegments = segments.filter(segment => segment.direction === 'UPWARD');
  const downwardSegments = segments.filter(segment => segment.direction === 'DOWNWARD');
  
  // Pre-calculate all heights to determine appropriate scaling
  let maxHeight = 0;
  const scaleFactor = 1; // Adjust this for height scaling
  
  // Calculate heights for all segments and find the maximum height
  upwardSegments.forEach(segment => {
    try {
      const startPrice = parseFloat(segment.startPrice);
      const endPrice = parseFloat(segment.endPrice);
      const totalValueUsd = parseFloat(segment.totalValueUsd);
      const width = Math.abs(endPrice - startPrice);
      
      // Calculate normalized height
      const height = (width > 0) ? Math.abs(totalValueUsd / width) * scaleFactor : 0;
      
      segment._calculatedHeight = height;
      maxHeight = Math.max(maxHeight, height);
    } catch (error) {
      console.error('Error calculating upward segment height:', error);
      segment._calculatedHeight = 0;
    }
  });
  
  downwardSegments.forEach(segment => {
    try {
      const startPrice = parseFloat(segment.startPrice);
      const endPrice = parseFloat(segment.endPrice);
      const totalValueUsd = parseFloat(segment.totalValueUsd);
      const width = Math.abs(endPrice - startPrice);
      
      // Calculate normalized height
      const height = (width > 0) ? Math.abs(totalValueUsd / width) * scaleFactor : 0;
      
      segment._calculatedHeight = height;
      maxHeight = Math.max(maxHeight, height);
    } catch (error) {
      console.error('Error calculating downward segment height:', error);
      segment._calculatedHeight = 0;
    }
  });
  
  // Chart options
  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      extraCssText:'z-index:100000000;position:fixed;top:-1000px;',
      appendToBody:true,
      confine:true,
      enterable: true,
      trigger: 'item',
      position: function(point, params, dom, rect, size) {
        // Use requestAnimationFrame to ensure tooltip is rendered before measuring
        const setPos=() => {
          // Get the tooltip DOM element
          const tooltipDom = dom;
          
          // Make sure tooltip is visible for measuring
          tooltipDom.style.opacity = "0";
          tooltipDom.style.visibility = "visible";
          
          // Measure tooltip dimensions
          const tooltipWidth = tooltipDom.offsetWidth;
          const tooltipHeight = tooltipDom.offsetHeight;
          
          // Get canvas position relative to the viewport
          const canvasRect = canvas.getBoundingClientRect();
          
          // Calculate position relative to viewport
          // Position tooltip to the right of cursor, adding canvas position
          let x = point[0] + canvasRect.left + 15;
          let y = point[1] + canvasRect.top - tooltipHeight/2; // Center vertically with cursor
          
          // Make sure tooltip stays within window bounds
          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;
          
          // Adjust if tooltip would go off right edge
          if (x + tooltipWidth > windowWidth) {
            x = point[0] - tooltipWidth - 15; // Position to left of cursor
          }
          
          // Adjust if tooltip would go off top/bottom edges
          if (y < 0) {
            y = 0;
          } else if (y + tooltipHeight > windowHeight) {
            y = windowHeight - tooltipHeight;
          }
          
          // Set tooltip styles
          tooltipDom.style.position = 'fixed';
          tooltipDom.style.left = x + 'px';
          tooltipDom.style.top = y + 'px';
          tooltipDom.style.pointerEvents = 'auto';
          tooltipDom.style.opacity = "1";
        };
        setPos();
        setTimeout(setPos, 0);
        requestAnimationFrame(setPos);
        // Return null to prevent default positioning
        return [0, 0];
      },
      formatter: function(params) {
        // Get the segment data from the params.data which we'll pass to the series
        const segmentData = params.data;
        
        if (segmentData) {
          const formattedValue = formatNumberM(parseFloat(segmentData.totalValueUsd));
          return `流动性: ${formattedValue}$`;
        }
        return 'test';
      }
    },
    xAxis: {
      type: 'value',
      min: minPrice,
      max: maxPrice,
      splitNumber: splitNumber,
      axisLine: {
        show: true,
        lineStyle: {
          color: '#999'
        }
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
        length: 3,
        lineStyle: {
          color: '#999'
        }
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: ['#eee'],
          type: 'dashed'
        }
      },
      minorTick: {
        show: true,
        splitNumber: 5,
        length: 2,
        lineStyle: {
          color: '#ddd'
        }
      },
      minorSplitLine: {
        show: true,
        lineStyle: {
          color: '#eee',
          type: 'dotted',
          opacity: 0.3
        }
      },
      axisLabel: {
        formatter: function(value) {
          // Determine the precision based on the value
          let precision = 6;
          const absValue = Math.abs(value);
          if (absValue >= 1000) {
            precision = 0;
          } else if (absValue >= 100) {
            precision = 1;
          } else if (absValue >= 10) {
            precision = 2;
          } else if (absValue >= 1) {
            precision = 3;
          } else if (absValue >= 0.1) {
            precision = 4;
          } else if (absValue >= 0.01) {
            precision = 5;
          }
          return value.toFixed(precision);
        },
        margin: 10,
        showMinLabel: true,
        showMaxLabel: true,
        rotate: 0
      }
    },
    yAxis: {
      type: 'value',
      name: '流动性',
      nameLocation: 'middle',
      nameGap: 30,
      max: maxHeight * 1.3,
      min: 0,
      splitNumber: 5,
      show: false,
      axisLine: {
        show: true
      },
      axisLabel: {
        show: true,
        inside: false,
        formatter: function(value) {
          // Remove the scaling to directly show values
          return formatNumberM(value);
        }
      },
      splitLine: {
        show: true
      }
    },
    series: [
      {
        name: '买盘',
        type: 'custom',
        renderItem: function(params, api) {
          // Get the segment by index from our filtered array
          const segment = upwardSegments[params.dataIndex];
          if (!segment) return;
          
          const startPrice = parseFloat(segment.startPrice);
          const endPrice = parseFloat(segment.endPrice);
          // Use the pre-calculated height
          const height = segment._calculatedHeight || 0;
          
          const x0 = api.coord([startPrice, 0])[0];
          const y0 = api.coord([0, 0])[1];
          const x1 = api.coord([endPrice, 0])[0];
          const y1 = api.coord([0, height])[1];
          
          const rectShape = {
            x: x0,
            y: y1,
            width: x1 - x0,
            height: y0 - y1
          };
          
          return {
            type: 'rect',
            shape: rectShape,
            style: api.style({
              fill: 'rgba(0, 200, 0, 0.7)'
            })
          };
        },
        // Pass the actual segment data instead of just 1
        data: upwardSegments,
        z: 10
      },
      {
        name: '卖盘',
        type: 'custom',
        renderItem: function(params, api) {
          // Get the segment by index from our filtered array
          const segment = downwardSegments[params.dataIndex];
          if (!segment) return;
          
          const startPrice = parseFloat(segment.startPrice);
          const endPrice = parseFloat(segment.endPrice);
          // Use the pre-calculated height
          const height = segment._calculatedHeight || 0;
          
          const x0 = api.coord([startPrice, 0])[0];
          const y0 = api.coord([0, 0])[1];
          const x1 = api.coord([endPrice, 0])[0];
          const y1 = api.coord([0, height])[1];
          
          const rectShape = {
            x: x0,
            y: y1,
            width: x1 - x0,
            height: y0 - y1
          };
          
          return {
            type: 'rect',
            shape: rectShape,
            style: api.style({
              fill: 'rgba(200, 0, 0, 0.7)'
            })
          };
        },
        // Pass the actual segment data instead of just 1
        data: downwardSegments,
        z: 10
      }
    ]
  };
  
// liquidityInfo.currentPrice
  
  // Set chart options and render
  chart.setOption(option);
  
  // Handle resize
  window.addEventListener('resize', function() {
    if (chart) {
      chart.resize();
    }
  });
  
  // Return the chart instance for further customization if needed
  return chart;
}; 